import { versionCookie } from '@libs/client/helpers/constant'
import { NextFunction, Request, Response } from 'express'
import { excluded, parseCookie, resolvePageVersionTraffic } from '../ultilities'
import { store_type } from '@libs/common/models/constant'

// Local expiry helper (bvf has no @ui/helpers/date getExpiresDate). Returns a Date
// `days` in the future for sticky-cookie `expires`. Server-only — safe to use new Date().
const getExpiresDate = (days: number) => new Date(Date.now() + days * 24 * 60 * 60 * 1000)

const PVERSION_COOKIE_OPTS = { expires: getExpiresDate(7), sameSite: 'lax' as const, secure: true }

/**
 * Split traffic for the page addressed by `subpath` and pin the chosen page version to a
 * sticky `pversion_<slug>` cookie, mirroring cms-template-default's redirect middleware:
 * - `--content-version` query param forces a version (preview).
 * - No cookie yet (or a cookie that doesn't reference this page): run the weighted split
 *   (resolvePageVersionTraffic) and set the cookie so the visitor keeps that version.
 * - Existing cookie: keep it, unless the 2-version A/B `traffic_config` changed since the
 *   last visit (`current_config`), in which case re-split and flag the cart for reset.
 * The chosen id is also appended to `req.headers.cookie` so the SAME request's SSR render
 * (initializeState → applyPageVersion) reads the freshly-picked version.
 */
const applyPageVersionCookie = (
  req: Request,
  res: Response,
  subpath: string,
  stardardHost: string
) => {
  const cookieClient = parseCookie(req?.headers?.cookie || '')
  const store_id = process._repository?.domains?.[stardardHost]?.store_id || ''
  const store = process._repository?.stores?.[store_id]
  if (!store) return

  const storeType = store?.setting?.settings?.general?.type
  const slugOnly =
    storeType === store_type.SELLPAGE
      ? ''
      : Object.keys(store?.pages || {}).find((slug) => subpath.split('/').includes(slug)) || ''
  // Only product pages carry a version. The MINISTORE homepage (no slug match) sits out.
  if (storeType !== store_type.SELLPAGE && !slugOnly) return

  const pageId =
    storeType === store_type.SELLPAGE
      ? ((store?.pages as unknown) as S3Types.page)?.setting?.settings?.general?.id
      : store?.pages?.[slugOnly]?.setting?.settings?.general?.id
  const key = `pversion${slugOnly ? `_${slugOnly}` : ''}`
  const pVersionCookie = cookieClient?.[key]

  const previewVersion = req?.query?.['--content-version']?.toString()
  if (previewVersion) {
    req.headers.cookie += `; ${key}=${previewVersion}`
    res.cookie(key, previewVersion, PVERSION_COOKIE_OPTS)
    res.setHeader(key, previewVersion)
    return
  }

  const page = process._repository.pages?.[pageId || '']
  const allVersionConfig = Object.keys(page?.setting?.settings?.traffic_config || {})
  if (!allVersionConfig.length) return

  if (!pVersionCookie || !allVersionConfig.includes(pVersionCookie)) {
    const pageVersion = resolvePageVersionTraffic(page)
    req.headers.cookie += `; ${key}=${pageVersion}`
    res.cookie(key, pageVersion, PVERSION_COOKIE_OPTS)
    res.setHeader(key, pageVersion)
  } else if (
    allVersionConfig.length === 2 &&
    cookieClient?.['current_config'] !== allVersionConfig.join('-')
  ) {
    // The A/B roster changed since this visitor was assigned: re-split and reset the cart
    // so a stale in-cart version isn't checked out against the new config.
    const chooseVersion = resolvePageVersionTraffic(page)
    req.headers.cookie += `; ${key}=${chooseVersion}`
    res.cookie(key, chooseVersion, PVERSION_COOKIE_OPTS)
    res.setHeader(key, chooseVersion)
    res.cookie('is_clear_cart', 'true', { sameSite: 'lax', secure: true })
  } else {
    res.setHeader(key, pVersionCookie)
  }
  res.cookie('current_config', allVersionConfig.join('-'), PVERSION_COOKIE_OPTS)
}

export const redirect = async (req: Request, res: Response, next: NextFunction) => {
  try {
    const subpath = req?.url.split('?').length ? req?.url.split('?')[0] : ''
    if (subpath?.includes('//')) {
      res.statusCode = 400
      res.send('BadRequest')
      return
    }
    if (req?.url && !excluded(req?.url)) {
      const host = req.hostname
      const stardardHost = host.replace(/^http:/g, '^https:').replace(/www\./g, '')
      if (host != stardardHost) {
        res.redirect(`https://${stardardHost}${req?.url ?? ''}`)
        return
      } else {
        const userCookie = parseCookie(req?.headers?.cookie || '')?._version_
        if (!userCookie) {
          let cookieValue = ''
          if (req?.url.includes('/cupi')) {
            const randomPercent = Math.random()
            cookieValue = randomPercent < 0 ? 'bv10' : 'bv14'
          } else {
            cookieValue = 'bv10'
          }
          cookieValue = versionCookie?.includes(cookieValue) ? cookieValue : 'bv10'
          req.headers.cookie += `; _version_=${cookieValue}`
          res.cookie('_version_', cookieValue)
          res.setHeader('version', cookieValue)
        } else {
          if (versionCookie?.includes(userCookie)) {
            res.setHeader('version', userCookie)
          } else {
            req.headers.cookie += `; _version_=bv10`
            res.cookie('_version_', 'bv10')
            res.setHeader('version', 'bv10')
          }
        }

        // Page-version traffic split + sticky pversion_<slug> cookie (product pages only).
        if (!req?.url.includes('api/')) {
          applyPageVersionCookie(req, res, subpath, stardardHost)
        }
      }
    }
    next()
  } catch (error) {
    console.error(error, req.url, req?.headers)
    next()
  }
}
